import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../../utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // Named so a test can assert the app chrome does not paint through it.
    // The header once carried a higher z-index than this scrim, which left the
    // top bar bright over a dimmed page.
    data-testid="overlay-scrim"
    className={cn(
      "fixed inset-0 z-50 bg-hb-dusk-deep/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogFixedFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex bottom-0 left-0 right-0 justify-end space-x-2 bg-background p-4 sm:rounded-b-2xl",
      className,
    )}
    {...props}
  />
);
DialogFixedFooter.displayName = "DialogFixedFooter";

/**
 * A dialog's buttons, pinned below the scroll area so they stay in reach
 * however long the form is, for example on a phone in landscape. Its own
 * wrapper with a top border on the dialog's card colour, around the same
 * layout as DialogFooter. Like DialogFixedFooter it must be a direct child of
 * DialogContent, which places either below the scroll area.
 *
 * It does not account for an open on-screen keyboard: the dialog's height is
 * 85vh of the layout viewport (no dvh, no interactive-widget setting), so the
 * keyboard can still cover the footer.
 */
const DialogPinnedFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid="dialog-pinned-footer"
    className="border-t border-border bg-card px-6 py-4 sm:rounded-b-2xl"
  >
    <div
      className={cn(
        "flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  </div>
);
DialogPinnedFooter.displayName = "DialogPinnedFooter";

const isPinned = (child: React.ReactNode) => {
  const type = (child as React.ReactElement | null)?.type;
  return type === DialogFixedFooter || type === DialogPinnedFooter;
};

export interface DialogProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  hideClose?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogProps
>(({ className, children, hideClose, ...props }, ref) => {
  const childrenArray = React.Children.toArray(children);
  const dialogFixedFooter = childrenArray.find(isPinned);
  const remainingChildren = childrenArray.filter((child) => !isPinned(child));

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] flex-col w-[calc(100%-2rem)] max-w-[640px] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-modal duration-200 dark:border-[length:var(--lit-edge-width)] dark:border-lit-edge dark:shadow-lit-strong data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      >
        {/* The content is a flex column with only a max-height, so a
            percentage height never resolves here. The scroll area takes the
            space left over (flex-1) and may shrink below its content
            (min-h-0), which is what makes it scroll instead of being clipped
            by the overflow-hidden above. A DialogFixedFooter or
            DialogPinnedFooter sits below it and stays in view. */}
        <div
          data-testid="dialog-scroll"
          className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6"
        >
          {remainingChildren}
        </div>
        {dialogFixedFooter}

        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 flex items-center justify-center rounded-sm p-2 max-sm:right-2 max-sm:top-2 max-sm:size-11 opacity-70 ring-offset-background transition-opacity hover:bg-muted hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex w-full flex-col space-y-2 text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-tight text-foreground",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogFixedFooter,
  DialogPinnedFooter,
};
